import React, {useCallback, useEffect, useState} from 'react'
import {Box} from '@mui/material'
import DataTable, {PaginationState} from 'src/components-new/data/DataTable'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import SearchBar from 'src/components-new/inputs/SearchBar'
import {STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import {buildColumns, CustomerUserRow} from './customerUsersColumns'
import CustomerUserDetailDialogNew from './CustomerUserDetailDialogNew'

const ROWS_PER_PAGE = 10

// The legacy filter config only ever exposed firstName/lastName as two separate AND-ed
// backend filters — there's no PSP-configurable "filters" key for this section (only
// columns are PSP-driven, via customerUtilitiesConfigurations.columnOptions). Since the
// only two fields are just the two halves of a person's name, a single free-text search
// matching either one (like PatientsListNew's search) is a better fit than a two-field
// drawer. The backend can't OR firstName/lastName in one request, so — same reasoning as
// PatientsListNew — search and pagination both happen client-side over a single large
// fetch instead of round-tripping to the server per keystroke/page.
const FETCH_SIZE = 200

/**
 * New-style equivalent of src/pages/customerUsers/CustomerUsers.tsx. No page title/
 * breadcrumb is rendered here — HeaderBar derives it from the "customerUtilities" nav
 * item label. A customer user may edit only their OWN record (readOnly is computed by
 * comparing the selected row's authUserId with the logged-in user's authUserId) — every
 * other record opens the same dialog in view-only mode.
 */
export default function CustomerUsersListNew(): JSX.Element {
  const columnConfig = useAppSelector(state => state.pspConfigReducer.data.customerUtilitiesConfigurations?.columnOptions)
  const currentUser = useAppSelector(state => state.userReducer.data)

  const [rows, setRows] = useState<CustomerUserRow[]>([])
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: ROWS_PER_PAGE})
  const [selectedCustomerUser, setSelectedCustomerUser] = useState<CustomerUserRow>()
  const [dialogOpen, setDialogOpen] = useState(false)

  const fetchData = useCallback(() => {
    apiClient.getClients([], setStatus, {page: 0, rowsPerPage: FETCH_SIZE, order: []})
      .then((data: any) => {
        if (Array.isArray(data)) setRows(data)
      })
      .catch(() => setStatus(STATUS.REJECTED))
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleSearchChange = (value: string) => {
    setSearch(value)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handleRowClick = (row: CustomerUserRow) => {
    setSelectedCustomerUser(row)
    setDialogOpen(true)
  }

  const handleSave = async (id: string, body: any) => {
    // apiClientRequest swallows the error and just flips setStatus to REJECTED — it
    // never rethrows and patchClients never returns a response body — so the only way
    // to know the PATCH failed is to read the status this local callback captures
    // synchronously, right after the awaited call settles.
    let patchStatus: string | undefined
    await apiClient.patchClients(id, body, (s: string) => { patchStatus = s })
    if (patchStatus === STATUS.REJECTED) {
      throw new Error('Errore durante il salvataggio')
    }
    await fetchData()
  }

  // Matched per word instead of as one "firstName lastName" string, so it doesn't matter
  // whether the two words are typed as "Nome Cognome" or "Cognome Nome" (the Cliente
  // column itself displays cognome-first) — every typed word just has to show up in
  // either field, in any order.
  const filteredRows = search.trim()
    ? rows.filter(row => {
      const firstName = (row.firstName ?? '').toLowerCase()
      const lastName = (row.lastName ?? '').toLowerCase()
      const searchTokens = search.trim().toLowerCase().split(/\s+/)
      return searchTokens.every(token => firstName.includes(token) || lastName.includes(token))
    })
    : rows

  const pagedRows = filteredRows.slice(
    pagination.page * pagination.rowsPerPage,
    (pagination.page + 1) * pagination.rowsPerPage,
  )

  const columns = buildColumns(columnConfig)
  const readOnly = selectedCustomerUser?.authUserId !== currentUser?.authUserId

  return (
    <Box>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap'}}>
        <SearchBar
          value={search}
          onChange={handleSearchChange}
          placeholder="Cerca per nome o cognome…"
          sx={{flex: '1 1 240px', maxWidth: 360}}
        />
      </Box>

      {status === STATUS.REJECTED && (
        <InlineMessage
          severity="error"
          onClose={() => setStatus(STATUS.FULFILLED)}
          sx={{mb: 2}}
        >
          {'Errore nel recuperare le utenze cliente.'}
        </InlineMessage>
      )}

      <DataTable<CustomerUserRow>
        columns={columns}
        rows={pagedRows}
        keyField="_id"
        loading={status === STATUS.PENDING}
        emptyMessage="Nessuna utenza cliente trovata"
        onRowClick={handleRowClick}
        totalElements={filteredRows.length}
        pagination={pagination}
        onPaginationChange={setPagination}
      />

      {selectedCustomerUser && (
        <CustomerUserDetailDialogNew
          customerUser={selectedCustomerUser}
          readOnly={readOnly}
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSave={handleSave}
        />
      )}
    </Box>
  )
}
